/* «Создать кавер» на движке шаблона: чистые функции без DOM (план вехи 3, шаг 2; lane plan text-cover §2.1 «Inputs ↔ S»).
   Состояние формы шаблона (S движка) — вид входов сервиса заказа: readCover(входы) → вид, writeCover(вид, прежние входы) → входы,
   только опубликованными ключами (patchSpec, cover-policy.ts): style_id | own_style, style_text, duration, from_ms, voice_mode, voice_id,
   source_order_id. Ключ песни (song_upload_id или поле шаблона) пишут мост загрузки и сервис, rights_confirmed — лист согласия: здесь их нет.
   Списки шаблона (STY, DURS, CHIPS, THEMES) — из политики формы constraints.cover (coverPolicy); id стиля движка — число (шаблон сравнивает
   id числом: +id), id сервера — строка поля style_id: coverLists держит обе стороны. */
import type { StudioForm } from '../studio/studio-api';
import { record, rows, text } from '../studio/studio-content';
import { patchSpec, readText, specKey } from '../reels/form-keys';
import { coverPolicy, type CoverDuration, type CoverPolicy } from './cover-policy';

type Inputs = Record<string, string>;
/** Обложка без цвета стиля: монохром «Своего стиля» шаблона (OWN.c) */
export const NEUTRAL_ART: readonly [string, string, string] = ['#d4d4d8', '#52525b', '#18181b'];
/** Стиль в виде шаблона (STY): id движка (число), sid — id сервера, n, d, c [свет, тень, фон], th — темы, pop, isNew, badge (hit | new | '') */
export interface CoverEngineStyle {
  id: number;
  sid: string;
  n: string;
  d: string;
  c: [string, string, string];
  th: string[];
  pop: number;
  isNew: boolean;
  badge: 'hit' | 'new' | '';
  preview: string | null;
}
export interface CoverLists {
  policy: CoverPolicy;
  styles: CoverEngineStyle[];
  durations: CoverDuration[];
  /** «Свой стиль» опубликован (own_style) */
  own: boolean;
  /** Стиль по умолчанию (id движка или own), если входы стиля не задают */
  defaultStyle: number | 'own' | null;
  defaultDuration: 'frag' | 'full';
  /** Окно фрагмента, с */
  fragSec: number;
}
/** Вид формы (часть S движка): стиль, текст поля текущего стиля, длительность, начало фрагмента (с), голос, трек «Моих треков» */
export interface CoverView {
  sty: number | 'own';
  /** style_text: у «Своего стиля» — описание (S.desc), у остальных — пожелания (S.text) */
  text: string;
  dur: 'frag' | 'full';
  from: number;
  vm: 'src' | 'voice';
  voice: number | null;
  source: number | null;
}

const color = (value: unknown) => (typeof value === 'string' && /^#[0-9a-f]{3,8}$/i.test(value) ? value : null);
const positive = (value: unknown) => {
  const n = Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isSafeInteger(n) && n > 0 ? n : null;
};

/** Списки шаблона из формы; форма без кавера — null */
export function coverLists(form: StudioForm): CoverLists {
  const policy = coverPolicy(form);
  const cover = record(record(form.constraints).cover);
  const raw = new Map(rows(cover.styles).map(s => [typeof s.id === 'number' ? String(s.id) : text(s.id), s] as const));
  const used = new Set<number>();
  const styles = policy.styles.map((s, i): CoverEngineStyle => {
    const numeric = /^\d{1,9}$/.test(s.id) ? Number(s.id) : null;
    /* id сервера не число или повторился — свой номер движка (шаблон сравнивает id числом); в заказ уходит sid */
    const id = numeric !== null && numeric > 0 && !used.has(numeric) ? numeric : 1_000_000 + i;
    used.add(id);
    const tint = record(raw.get(s.id)?.tint);
    return {
      id,
      sid: s.id,
      n: s.name,
      d: s.description,
      c: [s.color ?? NEUTRAL_ART[0], color(tint.to) ?? NEUTRAL_ART[1], color(tint.bg) ?? NEUTRAL_ART[2]],
      th: s.themes,
      pop: s.popularity,
      isNew: s.isNew,
      badge: s.hit ? 'hit' : s.isNew ? 'new' : '',
      preview: s.previewUrl,
    };
  });
  const bySid = (sid: string) => styles.find(s => s.sid === sid)?.id ?? null;
  const field = specKey(form, 'style_id');
  const defaultStyle =
    bySid(field?.defaultValue ?? '') ??
    bySid(cover.default_style_id === undefined || cover.default_style_id === null ? '' : String(cover.default_style_id)) ??
    styles[0]?.id ??
    (policy.ownStyle ? 'own' : null);
  const durations = policy.durations;
  const ids = durations.map(d => d.id);
  const durField = specKey(form, 'duration');
  const wanted = [durField?.defaultValue, text(cover.default_duration), rows(cover.durations).find(d => d.default === true)?.id, 'frag'];
  const defaultDuration = (wanted.find((id): id is 'frag' | 'full' => (id === 'frag' || id === 'full') && ids.includes(id)) ?? ids[0] ?? 'frag') as 'frag' | 'full';
  const frag = durations.find(d => d.id === 'frag');
  return { policy, styles, durations, own: policy.ownStyle, defaultStyle, defaultDuration, fragSec: Math.max(1, Math.round((frag?.windowMs ?? 60000) / 1000)) };
}

/** id стиля движка для id сервера; неизвестный — null */
export const styleBySid = (lists: CoverLists, sid: string) => lists.styles.find(s => s.sid === sid)?.id ?? null;
/** id сервера для id стиля движка; неизвестный — null */
export const sidOf = (lists: CoverLists, id: unknown) => lists.styles.find(s => String(s.id) === String(id))?.sid ?? null;

/** Входы → вид формы. Незнакомый стиль — стиль по умолчанию; незнакомая длительность — по умолчанию; «Другой голос» без id голоса — оригинал */
export function readCover(form: StudioForm, lists: CoverLists, inputs: Inputs): CoverView {
  const info = (key: string) => specKey(form, key);
  const own = lists.own && readText(info('own_style'), inputs) === 'true';
  const sid = readText(info('style_id'), inputs);
  const sty = own ? 'own' : (styleBySid(lists, sid) ?? lists.defaultStyle ?? 'own');
  const raw = readText(info('duration'), inputs);
  const dur = (raw === 'frag' || raw === 'full') && lists.durations.some(d => d.id === raw) ? raw : lists.defaultDuration;
  const fromMs = positive(readText(info('from_ms'), inputs));
  const voice = positive(readText(info('voice_id'), inputs));
  const vm = readText(info('voice_mode'), inputs) === 'voice' && voice !== null ? 'voice' : 'src';
  return {
    sty,
    text: readText(info('style_text'), inputs),
    dur,
    from: dur === 'frag' && fromMs !== null ? Math.round(fromMs / 1000) : 0,
    vm,
    voice: vm === 'voice' ? voice : null,
    source: positive(readText(info('source_order_id'), inputs)),
  };
}

/** Вид формы → входы (поверх prev, только опубликованные ключи). songSec — длина песни, с (окно фрагмента не выходит за неё); null — неизвестна */
export function writeCover(form: StudioForm, lists: CoverLists, view: CoverView, prev: Inputs, o: { songSec: number | null }): Inputs {
  const own = view.sty === 'own';
  const at =
    view.dur === 'frag' ? (o.songSec !== null && o.songSec > 0 ? Math.max(0, Math.min(view.from, o.songSec - lists.fragSec)) : view.from) : 0;
  return patchSpec(form, prev, {
    own_style: own ? true : null,
    style_id: own ? null : sidOf(lists, view.sty),
    style_text: view.text.trim() ? view.text : null,
    duration: lists.durations.some(d => d.id === view.dur) ? view.dur : null,
    from_ms: at > 0 ? Math.round(at) * 1000 : null,
    voice_mode: view.vm,
    voice_id: view.vm === 'voice' && view.voice !== null ? String(view.voice) : null,
    source_order_id: view.source,
  });
}
